import type { GridCell } from '@glideapps/glide-data-grid';
import { toCsv } from '@nemo/core/model';
import type { GridPreferences } from './useGridPreferences.js';

/**
 * Somewhere a cell leads. An `href` starting with `/` is a page of this app and
 * is followed in place; anything else opens in a new tab.
 */
export interface GridLink {
  readonly label: string;
  readonly href: string;
}

/**
 * One column of any grid page: how it paints, what a copy, a search or an
 * export reads off it, and how it sorts. Everything a particular page knows
 * beyond that (what an edit writes, what a paste parses) is that page's own
 * extension of this.
 */
export interface GridColumnDef<Row, Context = undefined> {
  readonly id: string;
  readonly title: string;
  readonly width: number;
  readonly cell: (row: Row, context: Context) => GridCell;
  /** What a copy, an export and the search box all see. */
  readonly text: (row: Row, context: Context) => string;
  /** Absent where the header does not sort: a feed the server orders, an actions column. */
  readonly sortKey?: (row: Row, context: Context) => string | number | null;
  /**
   * What the column header carries beyond its title: where a value came from,
   * where that is the thing worth knowing before trusting it.
   */
  readonly note?: string;
  /**
   * Where a click on the cell goes. One link is followed; several raise a menu
   * of them at the pointer, because a canvas has no anchor per name.
   */
  readonly links?: (row: Row, context: Context) => readonly GridLink[];
  /**
   * Words set after a text cell's own text in the hint colour: what the figure
   * beside it needs said about it, which a plain string cannot colour.
   */
  readonly faintSuffix?: (row: Row, context: Context) => string;
  /** Enter on this column starts its editor instead of opening the row. */
  readonly editsOnEnter?: boolean;
}

export type SortDirection = 'asc' | 'desc';

export interface Sort {
  readonly id: string;
  readonly direction: SortDirection;
}

/** A header click: ascending, then descending, then the page's own order again. */
export function nextSort(current: Sort | null, id: string): Sort | null {
  if (current === null || current.id !== id) return { id, direction: 'asc' };
  return current.direction === 'asc' ? { id, direction: 'desc' } : null;
}

export function sortRows<Row, Context>(
  rows: readonly Row[],
  columns: readonly GridColumnDef<Row, Context>[],
  sort: Sort | null,
  context: Context,
): readonly Row[] {
  if (sort === null) return rows;
  const sortKey = columns.find((column) => column.id === sort.id)?.sortKey;
  if (sortKey === undefined) return rows;
  const sign = sort.direction === 'asc' ? 1 : -1;
  return [...rows].sort((left, right) => {
    const first = sortKey(left, context);
    const second = sortKey(right, context);
    // A blank cell sorts last whichever way the column is pointing, so
    // reversing the sort never fills the screen with empty rows.
    if (first === null || first === '') return second === null || second === '' ? 0 : 1;
    if (second === null || second === '') return -1;
    return sign * compare(first, second);
  });
}

function compare(left: string | number, right: string | number): number {
  if (typeof left === 'number' && typeof right === 'number') return left - right;
  return String(left).localeCompare(String(right));
}

/** The columns in the order this browser arranged them, new ones after their default neighbour. */
export function orderedColumns<Column extends { readonly id: string }>(
  columns: readonly Column[],
  preferences: GridPreferences,
): Column[] {
  const byId = new Map(columns.map((column) => [column.id, column]));
  return preferences.orderOf(columns.map((column) => column.id)).flatMap((id) => {
    const column = byId.get(id);
    return column === undefined ? [] : [column];
  });
}

/** The rows a search box keeps: any column's text containing what was typed. */
export function searchRows<Row, Context>(
  rows: readonly Row[],
  columns: readonly GridColumnDef<Row, Context>[],
  search: string,
  context: Context,
): readonly Row[] {
  const needle = search.trim().toLowerCase();
  if (needle === '') return rows;
  return rows.filter((row) =>
    columns.some((column) => column.text(row, context).toLowerCase().includes(needle)),
  );
}

/** A CSV of these rows as these columns read them. */
export function csvOf<Row, Context>(
  rows: readonly Row[],
  columns: readonly GridColumnDef<Row, Context>[],
  context: Context,
): string {
  return toCsv(
    columns.map((column) => column.title),
    rows.map((row) => columns.map((column) => column.text(row, context))),
  );
}
